import React, {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import './Dropdown.css';

interface DropdownOption {
  disabled: boolean;
  group?: string;
  hidden: boolean;
  label: React.ReactNode;
  searchLabel: string;
  value: string;
}

export interface DropdownProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'multiple' | 'size'> {
  menuPlacement?: 'auto' | 'up' | 'down';
  placeholder?: React.ReactNode;
  wrapperClassName?: string;
}

const textFromNode = (node: React.ReactNode): string => Children.toArray(node)
  .map((child) => typeof child === 'string' || typeof child === 'number' ? String(child) : '')
  .join('')
  .trim();

const optionFromElement = (element: React.ReactElement<React.OptionHTMLAttributes<HTMLOptionElement>>,
  group?: string): DropdownOption => ({
  disabled: Boolean(element.props.disabled),
  group,
  hidden: Boolean(element.props.hidden),
  label: element.props.children,
  searchLabel: textFromNode(element.props.children),
  value: String(element.props.value ?? textFromNode(element.props.children)),
});

const optionsFromChildren = (children: React.ReactNode): DropdownOption[] => {
  const options: DropdownOption[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'option') {
      options.push(optionFromElement(child as React.ReactElement<React.OptionHTMLAttributes<HTMLOptionElement>>));

      return;
    }
    if (child.type !== 'optgroup') return;
    const group = child as React.ReactElement<React.OptgroupHTMLAttributes<HTMLOptGroupElement>>;

    Children.forEach(group.props.children, (nested) => {
      if (isValidElement(nested) && nested.type === 'option') {
        options.push(optionFromElement(
          nested as React.ReactElement<React.OptionHTMLAttributes<HTMLOptionElement>>,
          group.props.label,
        ));
      }
    });
  });

  return options;
};

const enabledIndex = (options: DropdownOption[], start: number, direction: 1 | -1): number => {
  if (!options.length) return -1;
  let index = start;

  for (let count = 0; count < options.length; count += 1) {
    index = (index + direction + options.length) % options.length;
    if (!options[index].disabled) return index;
  }

  return -1;
};

const menuPosition = (trigger: HTMLButtonElement,
  placement: DropdownProps['menuPlacement']): React.CSSProperties => {
  const rect = trigger.getBoundingClientRect();
  const margin = 8;
  const gap = 6;
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const opensUp = placement === 'up' || (placement !== 'down' && spaceBelow < 240 && spaceAbove > spaceBelow);
  const maxHeight = Math.max(96, Math.min(320, opensUp ? spaceAbove - gap : spaceBelow - gap));
  const width = Math.min(Math.max(rect.width, 160), window.innerWidth - margin * 2);
  const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));

  const verticalPosition: React.CSSProperties = opensUp
    ? { bottom: window.innerHeight - rect.top + gap }
    : { top: rect.bottom + gap };

  return {
    ...verticalPosition,
    left,
    maxHeight,
    width,
  };
};

const changeEvent = (value: string): React.ChangeEvent<HTMLSelectElement> => {
  const target = { value } as HTMLSelectElement;

  return { target, currentTarget: target } as React.ChangeEvent<HTMLSelectElement>;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const Dropdown: React.FC<DropdownProps> = ({
  children,
  className = '',
  disabled = false,
  id: suppliedId,
  menuPlacement = 'auto',
  onChange,
  onBlur,
  onFocus,
  onInvalid,
  placeholder,
  required = false,
  value,
  defaultValue,
  wrapperClassName = '',
  ...selectProps
}) => {
  const generatedId = useId();
  const id = suppliedId || `dropdown-${generatedId.replace(/:/g, '')}`;
  const options = useMemo(() => optionsFromChildren(children), [children]);
  const menuOptions = useMemo(() => options.filter((option) => !option.hidden), [options]);
  const controlled = value !== undefined;
  const initial = String(value ?? defaultValue ?? options[0]?.value ?? '');
  const [internalValue, setInternalValue] = useState(initial);
  const selectedValue = controlled ? String(value ?? '') : internalValue;
  const selected = options.find((option) => option.value === selectedValue) || options[0];
  const selectedIndex = Math.max(0, menuOptions.findIndex((option) => option.value === selectedValue));
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const [invalid, setInvalid] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef({ query: '', timer: 0 });

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);
  const show = useCallback((initialIndex = selectedIndex) => {
    if (disabled || !menuOptions.length) return;
    setActiveIndex(initialIndex);
    setOpen(true);
  }, [disabled, menuOptions.length, selectedIndex]);
  const choose = useCallback((option: DropdownOption) => {
    if (option.disabled) return;
    if (!controlled) setInternalValue(option.value);
    if (!required || option.value) setInvalid(false);
    onChange?.(changeEvent(option.value));
    close(true);
  }, [close, controlled, onChange, required]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const update = () => {
      if (triggerRef.current) setPosition(menuPosition(triggerRef.current, menuPlacement));
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [menuPlacement, open]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: Event) => {
      const target = event.target as Node;

      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) close();
    };

    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);

    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [close, open]);

  useEffect(() => {
    if (disabled) close();
  }, [close, disabled]);

  useEffect(() => {
    if (!required || selectedValue) setInvalid(false);
  }, [required, selectedValue]);

  useEffect(() => {
    if (open) document.getElementById(`${id}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, id, open]);

  // eslint-disable-next-line max-statements
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Tab') { close(); return; }
    if (event.key === 'Escape') {
      if (open) { event.preventDefault(); event.stopPropagation(); close(true); }

      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open && menuOptions[activeIndex]) choose(menuOptions[activeIndex]); else show();

      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) { show(); return; }
      const next = enabledIndex(menuOptions, activeIndex, event.key === 'ArrowDown' ? 1 : -1);
      if (next >= 0) setActiveIndex(next);

      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      if (!open) show();
      const next = enabledIndex(menuOptions, event.key === 'Home' ? -1 : 0, event.key === 'Home' ? 1 : -1);
      if (next >= 0) setActiveIndex(next);

      return;
    }
    if (event.key.length !== 1 || event.altKey || event.ctrlKey || event.metaKey) return;
    window.clearTimeout(typeahead.current.timer);
    typeahead.current.query += event.key.toLowerCase();
    const match = menuOptions.findIndex((option) => !option.disabled
      && option.searchLabel.toLowerCase().startsWith(typeahead.current.query));
    if (match >= 0) {
      if (open) setActiveIndex(match); else show(match);
    }
    typeahead.current.timer = window.setTimeout(() => { typeahead.current.query = ''; }, 500);
  };

  let previousGroup: string | undefined;

  return <span className={`site-dropdown ${wrapperClassName}`.trim()}>
    <button
      {...selectProps as React.ButtonHTMLAttributes<HTMLButtonElement>}
      ref={triggerRef}
      id={id}
      type="button"
      role="combobox"
      className={`site-dropdown__trigger ${className}`.trim()}
      aria-controls={open ? `${id}-listbox` : undefined}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-activedescendant={open ? `${id}-option-${activeIndex}` : undefined}
      aria-invalid={invalid || selectProps['aria-invalid'] || undefined}
      aria-required={required || selectProps['aria-required'] || undefined}
      disabled={disabled}
      onBlur={(event) => onBlur?.(event as unknown as React.FocusEvent<HTMLSelectElement>)}
      onFocus={(event) => onFocus?.(event as unknown as React.FocusEvent<HTMLSelectElement>)}
      onKeyDown={handleKeyDown}
      onClick={() => open ? close() : show()}
    >
      <span className="site-dropdown__value">{selected?.label || placeholder || 'Select'}</span>
      <svg className="site-dropdown__chevron" viewBox="0 0 12 12" aria-hidden="true">
        <path d="m2 4 4 4 4-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </svg>
    </button>
    <select
      {...selectProps}
      className={`site-dropdown__native ${className}`.trim()}
      aria-hidden="true"
      tabIndex={-1}
      disabled={disabled}
      required={required}
      value={selectedValue}
      onChange={onChange || (() => undefined)}
      onInvalid={(event) => {
        onInvalid?.(event);
        event.preventDefault();
        setInvalid(true);
        triggerRef.current?.focus({ preventScroll: true });
      }}
    >{children}</select>
    {open && createPortal(<ul
      ref={menuRef}
      id={`${id}-listbox`}
      role="listbox"
      className="site-dropdown__menu"
      data-dropdown-trigger-id={id}
      style={position}
    >
      {menuOptions.map((option, index) => {
        const groupChanged = option.group && option.group !== previousGroup;

        previousGroup = option.group;

        return <React.Fragment key={`${option.group || ''}-${option.value}-${index}`}>
          {groupChanged && <li className="site-dropdown__group" role="presentation">{option.group}</li>}
          <li
            id={`${id}-option-${index}`}
            role="option"
            aria-disabled={option.disabled || undefined}
            aria-selected={option.value === selectedValue}
            className={`site-dropdown__option${index === activeIndex ? ' is-active' : ''}`
              + `${option.value === selectedValue ? ' is-selected' : ''}${option.disabled ? ' is-disabled' : ''}`}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => !option.disabled && setActiveIndex(index)}
            onClick={() => choose(option)}
          >
            <span className="site-dropdown__check" aria-hidden="true">
              {option.value === selectedValue ? '✓' : ''}
            </span>
            <span className="site-dropdown__option-label">{option.label}</span>
          </li>
        </React.Fragment>;
      })}
    </ul>, document.body)}
  </span>;
};
